import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import useFetch from "../../../components/shared/useFetch";
import MarriageView from "./MarriageView";
import dayjs from "dayjs";

const MarriageRegisterChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ errorLogApi }] = useAtom(gbState);

  const url = `${process.env.REACT_APP_API_URL}/marriage/`;
  const { data: marriages, loading, error, refetch } = useFetch(url);

  const handleDelete = (row) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this Register?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = async (row) => {
    const userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/marriage/delete/${row._id}`,
        config
      );
      toast.success("Register Deleted Successfully.");
      refetch();
    } catch (err) {
      errorLogApi(err, "marriageChild", "/marriage");
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const filteredData = useMemo(() => {
    return (
      marriages?.filter((marriage) =>
        Object.values(marriage).some((value) =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      ) || []
    );
  }, [marriages, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="px-4 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Marriage Register</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaPlus />}
          onClick={() => navigate("/marriage/add")}
        >
          Add Marriage
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search marriages..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Record No.</TableCell>
              <TableCell>Marriage Date</TableCell>
              <TableCell>Groom</TableCell>
              <TableCell>Bride</TableCell>
              <TableCell>Marriage Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row._id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          toggleModal();
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/marriage/update", { state: row })
                        }
                      >
                        <FaEdit className="text-gray-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row)}
                      >
                        <FaTrash className="text-red-600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>{row.record_no}</TableCell>
                <TableCell>
                  {dayjs(row.marriage_date).utc().format("MM/DD/YYYY")}
                </TableCell>
                <TableCell>{row.groom_christian_name}</TableCell>
                <TableCell>{row.bride_christian_name}</TableCell>
                <TableCell>{row.marriage_location}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex justify-between items-center mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/marriage/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <MarriageView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default MarriageRegisterChild;
